import React, { useState } from 'react';
import styled from 'styled-components';

const Container = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const Heading = styled.h2`
  margin: 0;
  font-size: 40px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 700;
  display: inline-block;
  width: fit-content;
  background: linear-gradient(45deg, #fb7185, #fb923c);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;

  body.dark & {
    background: linear-gradient(45deg, #a3e635, #2dd4bf);
    background-clip: text;
    -webkit-background-clip: text;
  }
`;

const Entries = styled.div`
  display: flex;
  flex-direction: column;
`;

const Entry = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 0 40px;
  padding: 28px 0;
  border-top: 1px solid var(--card-border);

  &:last-child {
    border-bottom: 1px solid var(--card-border);
  }

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 4px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
`;

const DateRange = styled.span`
  font-size: 16px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 500;
  color: var(--text);
  letter-spacing: 0.04em;
  white-space: nowrap;
`;

const School = styled.h3`
  margin: 0;
  font-size: 22px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 700;
  line-height: 1.3;
  color: var(--text);
`;

const Degree = styled.span`
  font-size: 22px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 700;
  color: var(--text);
`;

const Detail = styled.span`
  font-size: 15px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 400;
  color: var(--text);
  opacity: 0.85;
`;

const DetailValue = styled.span`
  font-weight: 700;
  background: linear-gradient(90deg, #e5ff00, #00ffcc);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
`;

const Toggle = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 14px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 500;
  color: var(--text);
  opacity: 0.45;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: left;
  transition: opacity 0.2s ease;
  width: fit-content;
  margin-top: 4px;

  &:hover { opacity: 0.9; }
`;

const Courses = styled.ul<{ open: boolean }>`
  margin: 4px 0 0 0;
  padding: 0 0 0 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 24px;
  overflow: hidden;
  max-height: ${p => (p.open ? '600px' : '0')};
  opacity: ${p => (p.open ? 1 : 0)};
  transition: max-height 0.35s ease, opacity 0.25s ease;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const Course = styled.li`
  font-size: 15px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 400;
  color: var(--text);
  opacity: 0.85;
  line-height: 1.6;
`;

interface EducationEntry {
  school: string;
  dates: string;
  degree: string;
  details: string[];
  courses: string[];
}

const EDUCATION: EducationEntry[] = [
  {
    school: 'University of Southern California',
    dates: 'Aug 2022 – Aug 2024',
    degree: 'M.S. Computer Science (Honors)',
    details: ['GPA: 4.0'],
    courses: [
      'CSCI 585 — Database Systems',
      'CSCI 455x — Intro to Programming Systems Design',
      'CSCI 567 — Machine Learning',
      'CSCI 402 — Operating Systems',
      'EE 450 — Introduction to Computer Networks',
      'CSCI 596 — Scientific Computing and Visualization',
      'CSCI 580 — 3-D Graphics and Rendering',
      'CSCI 570 — Analysis of Algorithms',
      'CSCI 526 — Advanced Mobile Devices and Game Consoles',
    ],
  },
  {
    school: 'Indian Institute of Technology Kanpur',
    dates: 'Aug 2018 – May 2022',
    degree: 'B.Tech Mechanical Engineering',
    details: [
      'CGPA: 9.51',
      'Minor in Computer Science — GPA: 10.0',
      'Minor in Industrial Management & Engineering — GPA: 10.0',
    ],
    courses: [
      'CS 641A — Modern Cryptology',
      'CS 340A — Theory of Computation',
      'CS 685 — Data Mining',
      'ESO 207A — Data Structures & Algorithms',
      'ME 634A — Advanced Computational Fluid Dynamics',
      'ME 685A — Applied Numerical Methods',
    ],
  },
];

const EducationRow: React.FC<{ entry: EducationEntry }> = ({ entry }) => {
  const [open, setOpen] = useState(false);
  return (
    <Entry>
      <Meta>
        <School>{entry.school}</School>
      </Meta>
      <Body>
        <TitleRow>
          <Degree>{entry.degree}</Degree>
          <DateRange>{entry.dates}</DateRange>
        </TitleRow>
        {entry.details.map((d, j) => {
          const split = d.lastIndexOf(' ');
          return (
            <Detail key={j}>
              {d.slice(0, split + 1)}
              <DetailValue>{d.slice(split + 1)}</DetailValue>
            </Detail>
          );
        })}
        <Toggle onClick={() => setOpen(o => !o)}>
          {open ? '− courses' : '+ courses'}
        </Toggle>
        <Courses open={open}>
          {entry.courses.map((c, i) => <Course key={i}>{c}</Course>)}
        </Courses>
      </Body>
    </Entry>
  );
};

const Education: React.FC = () => (
  <Container>
    <Heading>Education</Heading>
    <Entries>
      {EDUCATION.map((e, i) => <EducationRow key={i} entry={e} />)}
    </Entries>
  </Container>
);

export default Education;
